import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    // Console log removed for production
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // You can also log the error to an error reporting service here
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
    
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRetry={this.handleRetry}
          variant={this.props.variant}
          title={this.props.title}
          message={this.props.message}
          showDetails={this.props.showDetails}
        />
      );
    }

    return this.props.children;
  }
}

const ErrorFallback = ({ 
  error, 
  errorInfo, 
  onRetry, 
  variant = 'default',
  title,
  message,
  showDetails = false 
}) => {
  const getVariantStyles = (variant) => {
    switch (variant) {
      case 'minimal':
        return {
          container: 'p-4 bg-crystal-white border-l-4 border-error',
          icon: 'h-5 w-5 text-error',
          title: 'text-sm font-medium text-error',
          message: 'text-sm text-steel',
          button: 'text-sm text-error hover:text-red-700 font-medium'
        };
      case 'card':
        return {
          container: 'bg-crystal-white shadow-minimal border border-silver-mist p-6',
          icon: 'h-8 w-8 text-error',
          title: 'text-lg font-medium text-deep-jungle',
          message: 'text-base text-steel',
          button: 'bg-error hover:bg-red-700 text-crystal-white px-4 py-2 text-sm font-medium border border-error transition-colors duration-200'
        };
      case 'full-screen':
        return {
          container: 'min-h-screen flex items-center justify-center bg-silver-mist px-4',
          icon: 'h-12 w-12 text-error',
          title: 'text-2xl font-bold text-deep-jungle',
          message: 'text-lg text-steel',
          button: 'bg-error hover:bg-red-700 text-crystal-white px-6 py-3 text-base font-medium border border-error transition-colors duration-200'
        };
      default:
        return {
          container: 'p-6 bg-crystal-white border-l-4 border-error shadow-minimal',
          icon: 'h-6 w-6 text-error',
          title: 'text-base font-medium text-error',
          message: 'text-sm text-steel',
          button: 'bg-error hover:bg-red-700 text-crystal-white px-3 py-2 text-sm font-medium border border-error transition-colors duration-200'
        };
    }
  };

  const styles = getVariantStyles(variant);
  const defaultTitle = title || 'Something went wrong';
  const defaultMessage = message || 'An unexpected error occurred. Please try again.';

  return (
    <div className={styles.container}>
      {variant === 'full-screen' ? (
        <div className="text-center max-w-md">
          <div className="mx-auto flex items-center justify-center h-16 w-16 bg-error/10 border border-error/20 mb-4">
            <svg className={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className={styles.title}>{defaultTitle}</h1>
          <p className={`${styles.message} mt-2`}>{defaultMessage}</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className={`${styles.button} mt-6`}
            >
              Try Again
            </button>
          )}
        </div>
      ) : (
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h3 className={styles.title}>{defaultTitle}</h3>
            <p className={`${styles.message} mt-1`}>{defaultMessage}</p>
            
            {showDetails && error && (
              <details className="mt-3">
                <summary className="cursor-pointer text-sm text-steel hover:text-graphite transition-colors duration-200">
                  Show error details
                </summary>
                <div className="mt-2 text-xs text-steel bg-silver-mist p-2 border border-silver-mist">
                  <pre className="whitespace-pre-wrap">
                    {error.toString()}
                    {errorInfo && errorInfo.componentStack}
                  </pre>
                </div>
              </details>
            )}
            
            {onRetry && (
              <div className="mt-4">
                <button
                  onClick={onRetry}
                  className={styles.button}
                >
                  Try Again
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ErrorBoundary;
